// src/redux/slices/conversationSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../../axios-client";
import { toast } from "react-toastify";

export const getParentConversationList = createAsyncThunk(
  "conversation/getParentConversationList",
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        pageId,
        pageSize,
        userId,
        searchText = "",
        opportunityFilter,
        userType,
        Unread,
        meetingDate,
        eventDate,
        guestCount,
        followupTask,
      } = params;

      const response = await axiosClient.get(
        `/message/getParentConversationListMessage`,
        {
          params: {
            pageId,
            pageSize,
            userId,
            searchText: searchText.trim(),
            opportunityFilter,
            userType,
            Unread,
            meetingDate,
            eventDate,
            guestCount,
            followupTask,
          },
        }
      );

      return {
        data: response.data.data || [],
        pageId: response.data.pageId || pageId,
        pageSize,
      };
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to load conversations";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

const initialState = {
  gridLoading: false,
  error: null,
  conversations: [],
  currentPage: 0,
  pageSize: 10,
  loadedPages: [], // CHANGED: Array instead of Set
  pendingPages: [], // CHANGED: Array instead of Set
  hasMore: true,
};

const conversationSlice = createSlice({
  name: "conversation",
  initialState,
  reducers: {
    resetConversations: (state) => {
      state.conversations = [];
      state.currentPage = 0;
      state.loadedPages = [];
      state.pendingPages = [];
      state.gridLoading = false;
      state.hasMore = true;
    },
    markConversationAsRead: (state, action) => {
      const chatId = action.payload;
      const conv = state.conversations.find((c) => c.Id === chatId);
      if (conv) {
        conv.unreadCount = 0;
      }
    },
    upsertIncomingMessage: (state, action) => {
      const msg = action.payload;
     
      const desc = msg.description ?? msg.latestDescription ?? msg.Description;
      const date = msg.createdAt ?? msg.messageDate ?? new Date().toISOString();
      const senderId = msg.createdBy;
      let conv = state.conversations.find((c) => c.Id === msg.parentId);
      if (conv) {
        // Update existing conversation
        conv.latestDescription = desc;
        conv.latestMessageDate = date;
        conv.unreadCount = (conv.unreadCount || 0) + 1;
        state.conversations = [
          conv,
          ...state.conversations.filter((c) => c.Id !== conv.Id),
        ];
      } else {
        // New conversation appeared
        const newConv = {
          Id: msg.parentId != 0 ? msg.parentId : msg.Id,
          otherPersonName:
            msg.senderName ??
            msg.createName ??
            msg.otherPersonName ??
            "Unknown",
          otherPersonPhoto: msg.senderPhoto ?? null,
          latestDescription: desc,
          latestMessageDate: date,
          unreadCount: 1, // new incoming message
          createdBy: senderId,
        };
        state.conversations.unshift(newConv);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Pending
      .addCase(getParentConversationList.pending, (state, action) => {
        const { pageId } = action.meta.arg;
        if (!state.pendingPages.includes(pageId)) {
          state.pendingPages.push(pageId);
        }
        state.gridLoading = true;
        // DO NOT TOUCH hasMore here
      })
      .addCase(getParentConversationList.fulfilled, (state, action) => {
        const { pageId, data, pageSize } = action.payload;

        state.pendingPages = state.pendingPages.filter((p) => p !== pageId);
        state.gridLoading = state.pendingPages.length > 0;

        if (!state.loadedPages.includes(pageId)) {
          state.loadedPages.push(pageId);
        }

        // Only set hasMore = false if we got less than pageSize
        if (data.length < pageSize) {
          state.hasMore = false;
        }

        if (pageId === 1) {
          state.conversations = data;
          state.currentPage = 1;
        } else {
          const existingIds = new Set(state.conversations.map((c) => c.Id));
          const newItems = data.filter((item) => !existingIds.has(item.Id));
          state.conversations.push(...newItems);
          state.currentPage = pageId;
        }
      })
      .addCase(getParentConversationList.rejected, (state, action) => {
        const { pageId } = action.meta.arg;
        state.pendingPages = state.pendingPages.filter((p) => p !== pageId);
        state.gridLoading = state.pendingPages.length > 0;
        state.error = action.payload;
      });
  },
});

// Actions
export const {
  resetConversations,
  markConversationAsRead,
  upsertIncomingMessage,
} = conversationSlice.actions;

// Safe fetch with deduplication
export const fetchParentConversations = (params) => {
  return async (dispatch, getState) => {
    const { pageId } = params;
    const state = getState().conversation;

    // Prevent duplicates using array.includes()
    if (
      state.loadedPages.includes(pageId) ||
      state.pendingPages.includes(pageId)
    ) {
      return;
    }

    await dispatch(getParentConversationList(params));
  };
};

export default conversationSlice.reducer;
